import { NODE_TYPE_LABELS, resolveNodeType } from './nodeConfig';
import {
  collectGroupMemberIds,
  nodeGroupLabel,
  normalizeNodeGroups,
  staleNodeIdsForGroup,
} from './nodeGroups';
import type { NodeType, ProjectDocument, ProjectNode, ProjectRoute } from '../types/project';

// ------------------------------------------------------------
// Elements list grouping (by type or by group)
// ------------------------------------------------------------

export type ElementsViewMode = 'type' | 'group' | 'manage';

export interface ElementsSection {
  id: string;
  label: string;
  nodes: ProjectNode[];
  routes: ProjectRoute[];
  staleNodeIds?: string[];
  groupId?: string;
  isEmptyGroup?: boolean;
}

const TYPE_ORDER: NodeType[] = [
  'input',
  'output',
  'splitter',
  'merger',
  'diverger',
  'processor',
  'gate',
  'waypoint',
];

function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
}

export function buildElementsSectionsByType(
  nodes: ProjectNode[],
  routes: ProjectRoute[],
): ElementsSection[] {
  const sections: ElementsSection[] = [];

  if (routes.length > 0) {
    sections.push({
      id: 'type:routes',
      label: 'Routes',
      nodes: [],
      routes: sortByName(routes),
    });
  }

  for (const type of TYPE_ORDER) {
    const typedNodes = nodes.filter((node) => resolveNodeType(node) === type);
    if (typedNodes.length === 0) continue;
    sections.push({
      id: `type:${type}`,
      label: NODE_TYPE_LABELS[type],
      nodes: sortByName(typedNodes),
      routes: [],
    });
  }

  const knownTypes = new Set(TYPE_ORDER);
  const otherNodes = nodes.filter((node) => !knownTypes.has(resolveNodeType(node) as NodeType));
  if (otherNodes.length > 0) {
    sections.push({
      id: 'type:other',
      label: 'Other',
      nodes: sortByName(otherNodes),
      routes: [],
    });
  }

  return sections;
}

export function buildElementsSectionsByGroup(
  document: ProjectDocument,
  nodes: ProjectNode[],
  routes: ProjectRoute[],
): ElementsSection[] {
  const sections: ElementsSection[] = [];
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const assignedNodeIds = new Set<string>();
  const assignedRouteIds = new Set<string>();

  for (const group of normalizeNodeGroups(document)) {
    const memberIds = collectGroupMemberIds(group, nodes);
    const groupNodes = [...memberIds]
      .map((id) => nodesById.get(id))
      .filter((node): node is ProjectNode => node != null);
    groupNodes.forEach((node) => assignedNodeIds.add(node.id));

    const groupRoutes = sortByName(
      routes.filter(
        (route) =>
          memberIds.has(route.from_node_id) || memberIds.has(route.to_node_id),
      ),
    );
    groupRoutes.forEach((route) => assignedRouteIds.add(route.id));

    const staleNodeIds = staleNodeIdsForGroup(group, nodes);
    const suffix =
      staleNodeIds.length > 0
        ? ` · ${staleNodeIds.length} stale`
        : groupNodes.length === 0
          ? ' · empty'
          : '';

    sections.push({
      id: `group:${group.id}`,
      label: `${group.name || group.id}${suffix}`,
      nodes: sortByName(groupNodes),
      routes: groupRoutes,
      staleNodeIds,
      groupId: group.id,
      isEmptyGroup: groupNodes.length === 0 && groupRoutes.length === 0,
    });
  }

  const ungroupedNodes = sortByName(
    nodes.filter((node) => {
      if (assignedNodeIds.has(node.id)) return false;
      return nodeGroupLabel(node).length === 0;
    }),
  );
  const ungroupedRoutes = sortByName(
    routes.filter((route) => !assignedRouteIds.has(route.id)),
  );

  if (ungroupedNodes.length > 0 || ungroupedRoutes.length > 0) {
    sections.push({
      id: 'group:ungrouped',
      label: 'Ungrouped',
      nodes: ungroupedNodes,
      routes: ungroupedRoutes,
    });
  }

  const labeledButUnassigned = sortByName(
    nodes.filter(
      (node) =>
        !assignedNodeIds.has(node.id) && nodeGroupLabel(node).length > 0,
    ),
  );
  if (labeledButUnassigned.length > 0) {
    sections.push({
      id: 'group:labeled',
      label: 'Labeled (not in group list)',
      nodes: labeledButUnassigned,
      routes: [],
    });
  }

  return sections;
}
